import { SerializedStyles } from "@emotion/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useFlags } from "launchdarkly-react-client-sdk";
import { ReactNode, Ref, memo, useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { usePhoneVerify } from "src/apps/AppFlow/pages/hooks/usePhoneVerify";
import { FormFlowPage, InfoAlert, TextInput } from "src/components";
import CheckboxSelector from "src/components/CheckboxSelector";
import { useModal } from "src/hooks";
import useMergedRef from "src/hooks/useMergedRef";
import { ProblemIllustration } from "src/images";
import { ResponseError } from "src/lib";
import { createStyles } from "src/styles";
import * as z from "zod";

import { enums } from "@fraction/shared";
import ModalBox from "../../../../components/ModalBox";

import { ModalInnards } from "src/components/Modal";
import { BoldText, RegularText } from "src/components/v1";
import { usePartialSubmit } from "../hooks";
import { styles } from "../styles";

const SCHEMA = z.object({
  phone: z
    .string()
    .regex(/^\(?([0-9]{3})\)?[-.●]?([0-9]{3})[-.●]?([0-9]{4})$/, "Please input a valid phone number"),
});

export interface ApplicantPhoneProps {
  applicantPriority?: number;
  value?: { phone?: string; contactMethodPreferences?: enums.ContactMethodPreference[] };
  onChange?: (value?: { phone?: string; contactMethodPreferences?: enums.ContactMethodPreference[] }) => void;
  buttonText?: string;
  children?: ReactNode;
  title?: string;

  focusRef?: Ref<HTMLInputElement>;
  scrollRef?: Ref<HTMLDivElement>;
  onNext: () => void;
  onBack: () => void;
  style?: SerializedStyles;
  inputStyle?: SerializedStyles;
}

const PAGE_STYLES = createStyles({
  alert: {
    marginBottom: "24px",
  },
  checkboxGroup: {
    marginBottom: "32px",
  },
});

const getModalText = (err: ResponseError): { header: string; paragraph: string } => {
  if (err.name === "IncorrectInputsError") {
    // we are in the phone section, so it's probably an incorrect phone number
    return {
      header: "Incorrect phone number",
      paragraph: "We were not able to validate your phone number. Please try a different number.",
    };
  }
  return {
    header: "Something went wrong",
    paragraph: "We could not start your application. Please try again.",
  };
};

const ApplicantPhone = ({
  value,
  onChange,
  onNext,
  onBack,
  style,
  inputStyle,
  focusRef,
  scrollRef,
  buttonText,
  children,
  title,
  applicantPriority,
  ...props
}: ApplicantPhoneProps) => {
  const { appFlowAskForContactMethodPreference } = useFlags();
  const { showModal } = useModal();
  const [showNumberValidation, setShowNumberValidation] = useState(false);
  const [validationNumber, setValidationNumber] = useState("");

  const { register, handleSubmit, errors } = useForm({
    resolver: zodResolver(SCHEMA),
  });

  const handleChangeContactMethodPreference = useCallback(
    (contactMethodPreferences: enums.ContactMethodPreference[]) => {
      onChange?.({
        ...value,
        contactMethodPreferences: contactMethodPreferences as enums.ContactMethodPreference[],
      });
    },
    [onChange, value]
  );

  const merged = useMergedRef(register, focusRef);
  const ref = focusRef ? merged : register;

  const validate = useCallback(() => !!value, [value]);
  const on4XXError = useCallback(
    (err: ResponseError) => {
      showModal({
        ...getModalText(err),
        image: <ProblemIllustration height={167} width={188} />,
      });
    },
    [showModal]
  );
  const { submitHandler, nextDisabled, nextLoading } = usePartialSubmit({ onNext, validate, on4XXError });

  const {
    startVerification,
    codeInvalidMessage,
    checkVerification,
    phoneAcceptable,
    phoneVerified,
    resetPhoneVerificationStatus,
  } = usePhoneVerify({
    phone: value?.phone,
    onSuccess: () => {
      setShowNumberValidation(false);
      handleSubmit(submitHandler)();
    },
  });

  const handleChangePhone = useCallback(
    (phone: string) => {
      resetPhoneVerificationStatus?.();
      return onChange?.({ ...value, phone });
    },
    [onChange, value, resetPhoneVerificationStatus]
  );

  const handleClickNext = useCallback(
    (e: React.ChangeEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!phoneVerified) {
        startVerification.mutate();
        setShowNumberValidation(true);
      } else {
        handleSubmit(submitHandler)();
      }
    },
    [startVerification, handleSubmit, submitHandler, phoneVerified]
  );

  const handleSubmitCode = useCallback(
    (e?: any) => {
      if (e && typeof e === "object" && "preventDefault" in e) {
        e?.preventDefault();
        e?.stopPropagation?.();
      }

      if (validationNumber?.length !== 6) {
        return;
      }
      return checkVerification.mutate({ code: validationNumber });
    },
    [validationNumber]
  );

  return (
    <div>
      <FormFlowPage
        {...props}
        title={title || "Where should we call you?"}
        description={
          <RegularText>
            Please provide a phone number that you have regular access to during business hours.
            {applicantPriority !== 0 ? (
              <BoldText>
                {" "}
                Don't worry, we won't ask for phone number verification for this number right now.
              </BoldText>
            ) : (
              ""
            )}
          </RegularText>
        }
        onNext={applicantPriority !== 0 || phoneVerified ? handleSubmit(submitHandler) : handleClickNext}
        onBack={onBack}
        style={style}
        nextDisabled={nextDisabled || !phoneAcceptable}
        buttonText={buttonText}
        nextLoading={nextLoading}
      >
        <TextInput
          error={errors?.phone?.message}
          style={[styles.input, inputStyle]}
          mask={["(999) 999-9999"]}
          name="phone"
          label="Your phone number"
          inputMode="tel"
          placeholder="(778) 123-4567"
          defaultValue={value?.phone || ""}
          onChange={handleChangePhone}
          type="tel-national"
          autoComplete="tel-national"
          ref={ref}
        />
        {appFlowAskForContactMethodPreference && (
          <CheckboxSelector
            style={PAGE_STYLES.checkboxGroup}
            focusRef={scrollRef}
            label="How would you prefer us to contact you?"
            options={(
              Object.keys(enums.ContactMethodPreference) as (keyof typeof enums.ContactMethodPreference)[]
            )
              .sort()
              .map((key) => ({
                value: enums.ContactMethodPreference[key],
                label: enums.ContactMethodPreference[key],
              }))}
            value={value?.contactMethodPreferences}
            onChange={handleChangeContactMethodPreference}
            errorMessage="Fraction requires at least one contact method to be selected."
          />
        )}
        <InfoAlert style={PAGE_STYLES.alert}>
          Don't worry, we hate spam calls too. We'll never share your number and will only call or text you
          about your Fraction application or account.
        </InfoAlert>
        {children}
        <ModalBox open={showNumberValidation} closeButton={false} {...props}>
          <ModalInnards
            actions={[
              {
                text: "Close",
                action: () => setShowNumberValidation(false),
              },
              {
                text: "Verify",
                action: handleSubmitCode,
                close: false,
                disabled: validationNumber?.length !== 6,
                loading: checkVerification.isPending,
              },
            ]}
            message="We've sent you a text to your phone to verify your number."
          >
            <form onSubmit={handleSubmitCode}>
              <TextInput
                onSubmit={handleSubmitCode}
                error={codeInvalidMessage}
                className="mt-4"
                style={[styles.input, inputStyle]}
                mask={["999999"]}
                name="phoneValidation"
                label="Enter the code we sent you"
                inputMode="numeric"
                onChange={setValidationNumber}
                value={validationNumber}
                autoComplete="one-time-code"
                ref={ref}
              />
            </form>
          </ModalInnards>
        </ModalBox>
      </FormFlowPage>
    </div>
  );
};

export default memo(ApplicantPhone);
